import { useEffect, useMemo, useRef, useState } from "react";
import { IconButton, Tab, Tabs } from "@mui/material";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckIcon from "@mui/icons-material/Check";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { useGetMyKazaQuery, useSetKazaDayMutation, useUpdateKazaSettingsMutation } from "../../api/personalApi";
import Drawer from "../../components/ui/Drawer";
import { SoftButton, cls } from "../hatim/soft/SoftKit";
import {
  KAZA_PRAYERS,
  MEZHEPS,
  PRAYER_LABEL_KEYS,
  addDaysToPrayers,
  canEditKazaDay,
  daysFromYears,
  liveRemaining,
  stepKazaDraft,
  emptyKazaCounts,
  prayersFor,
  sumKaza,
  toCount,
} from "../../lib/kaza";
import { todayString } from "../../lib/muzakereWeek";
import { formatTurkishDate, parseLocalDate } from "../../lib/dateUtils";
import "../profile/darkSurface.css";
import ConnectionState from "../../components/ui/ConnectionState";

const inputCls = `h-10 rounded-xl border border-terakkiBorder bg-terakkiSurface px-3 text-right text-md text-terakkiInk outline-none focus:border-terakkiPrimary ${cls.input}`;

function SectionTitle({ children }) {
  return <h2 className={`px-5 pt-5 pb-2 text-[13px] font-medium tracking-wide ${cls.muted}`}>{children}</h2>;
}

/**
 * Kaza çekmecesi: Terakkim'deki Kaza yuvarlağıyla açılır, iki sekme.
 * - Gün: seçili gün kılınan kazalar vakit vakit (mobil KazaDayModal);
 *   kaydedince fark kadar kalan kaza azalır. Bu haftanın Pazartesi'sinden
 *   bugüne kadar düzenlenebilir.
 * - Kaza borcum (mobil profile/kaza): mezhep ve her vaktin kalan sayısı;
 *   elle ya da "kaç yıl, kaç gün kılınmadı" ile tüm vakitlere eklenir.
 * Borç henüz girilmemişse doğrudan ikinci sekmeyle açılır.
 */
export default function KazaDrawer({ open, localDate, initialTab = 0, onClose, onSaved }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const { data, isLoading, isFetching, isError, error, refetch } = useGetMyKazaQuery({ localDate }, { skip: !open });
  const [save, { isLoading: saving }] = useUpdateKazaSettingsMutation();
  const [setDay, { isLoading: savingDay }] = useSetKazaDayMutation();
  const [tab, setTab] = useState(initialTab);
  const [dayDraft, setDayDraft] = useState({});
  const [mezhep, setMezhep] = useState("hanefi");
  const [counts, setCounts] = useState(emptyKazaCounts());
  const [saved, setSaved] = useState(null);
  const [years, setYears] = useState("");
  const [days, setDays] = useState("");

  // Her açılışta sunucudaki kayıttan başlanır; açıkken gelen tazelemeler taslağı bozmaz.
  const server = data?.data;
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setSaved(null);
      setTab(initialTab);
      setDayDraft(null);
    }
    wasOpen.current = Boolean(open);
  }, [open, initialTab]);
  useEffect(() => {
    if (open && server && dayDraft === null) setDayDraft(server.day || {});
  }, [open, server, dayDraft]);
  useEffect(() => {
    if (!server || saved) return;
    const initial = { mezhep: server.mezhep || "hanefi", remaining: { ...emptyKazaCounts(), ...server.remaining } };
    setMezhep(initial.mezhep);
    setCounts(initial.remaining);
    setSaved(initial);
  }, [server, saved]);

  const prayers = useMemo(() => prayersFor(mezhep), [mezhep]);
  const total = sumKaza(counts, prayers);
  const addDays = daysFromYears(years, days);
  const dirty =
    !!saved && (saved.mezhep !== mezhep || KAZA_PRAYERS.some((p) => (saved.remaining[p] || 0) !== (counts[p] || 0)));

  const applyYearsDays = () => {
    if (addDays <= 0) return;
    setCounts((prev) => addDaysToPrayers(prev, prayers, addDays));
    setYears("");
    setDays("");
  };

  const handleSave = async () => {
    const result = await save({ mezhep, remaining: counts });
    if (result.error) {
      toast.error(result.error.message || t("kaza_save_failed"));
      return;
    }
    const next = result.data?.data;
    const remaining = { ...emptyKazaCounts(), ...(next?.remaining || counts) };
    setCounts(remaining);
    setSaved({ mezhep: next?.mezhep || mezhep, remaining });
    toast.success(t("kaza_saved"));
  };

  const ready = !isLoading && !!saved;

  // Gün sekmesi
  const today = todayString();
  const dayEditable = Boolean(localDate) && canEditKazaDay(localDate, today);
  const dayPrayers = server ? server.prayers || prayersFor(server.mezhep) : [];
  const draft = dayDraft || {};
  const dayChanged = !!server && dayPrayers.some((p) => (draft[p] || 0) !== (server.day?.[p] || 0));
  const dateObj = localDate ? parseLocalDate(localDate) : null;
  const daySetUp = !!server?.isSetUp;

  const handleSaveDay = async () => {
    const result = await setDay({ localDate, today, counts: draft });
    if (result.error) {
      toast.error(result.error.message || t("kaza_day_save_failed"));
      return;
    }
    toast.success(t("kaza_day_saved"));
    setDayDraft(result.data?.data?.day || draft);
    if (onSaved) onSaved(result.data?.data);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="md"
      title={t("nav_kaza")}
      subtitle={
        ready ? `${t("kaza_total_remaining")}: ${total.toLocaleString("tr-TR")}` : t("kaza_total_remaining")
      }
      titleIcon={<BedtimeOutlinedIcon />}
      footer={
        <div className="flex gap-2">
          <SoftButton tone="soft" fullWidth onClick={onClose}>
            {t("manage_goals_close")}
          </SoftButton>
          {tab === 0 ? (
            dayEditable && daySetUp ? (
              <SoftButton
                tone="success"
                fullWidth
                startIcon={<CheckIcon />}
                disabled={!dayChanged || savingDay}
                onClick={handleSaveDay}
              >
                {dayChanged ? t("save") : t("calendar_saved")}
              </SoftButton>
            ) : null
          ) : (
            <SoftButton
              tone="success"
              fullWidth
              startIcon={<CheckIcon />}
              disabled={!ready || saving || !dirty}
              onClick={handleSave}
            >
              {dirty ? t("save") : t("calendar_saved")}
            </SoftButton>
          )}
        </div>
      }
    >
      <div className="tk-dark">
        <div className="border-b border-terakkiBorder dark:border-white/15 px-2">
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
            <Tab
              label={dateObj ? formatTurkishDate(dateObj) : t("kaza_day_title")}
              sx={{ minHeight: 46, textTransform: "none", fontSize: 15, fontWeight: 500 }}
            />
            <Tab label={t("kaza_debt_tab")} sx={{ minHeight: 46, textTransform: "none", fontSize: 15, fontWeight: 500 }} />
          </Tabs>
        </div>

        {tab === 0 ? (
          !daySetUp ? (
            <div className="flex flex-col items-center gap-3 py-10 px-6 text-center">
              <p className={`text-[15px] ${cls.muted}`}>{t("kaza_not_set_up")}</p>
              <SoftButton tone="soft" onClick={() => setTab(1)}>
                {t("kaza_debt_tab")}
              </SoftButton>
            </div>
          ) : (
            <div className="animate-fadeIn">
              <ul>
                {dayPrayers.map((p) => {
                  const count = draft[p] || 0;
                  const remaining = liveRemaining(server, draft, p);
                  return (
                    <li key={p} className={`flex items-center gap-3 px-5 py-3 ${cls.row}`} data-prayer={p}>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[15px] ${cls.title}`}>{t(PRAYER_LABEL_KEYS[p])}</div>
                        <div className={`text-[13px] ${cls.muted}`}>{`${t("kaza_remaining")}: ${remaining.toLocaleString("tr-TR")}`}</div>
                      </div>
                      {dayEditable ? (
                        <div className="flex items-center gap-2">
                          <IconButton
                            size="small"
                            aria-label={`${t(PRAYER_LABEL_KEYS[p])} ${t("kaza_decrease")}`}
                            disabled={count <= 0 || savingDay}
                            onClick={() => setDayDraft((prev) => stepKazaDraft(server, prev || {}, p, -1))}
                            className="!bg-terakkiBrandCloud dark:!bg-white/10"
                          >
                            <RemoveIcon fontSize="small" className={cls.accent} />
                          </IconButton>
                          <span
                            className={`w-8 text-center text-md ${count > 0 ? `font-medium ${cls.accent}` : cls.muted}`}
                            data-testid={`kaza-count-${p}`}
                          >
                            {count}
                          </span>
                          <IconButton
                            size="small"
                            aria-label={`${t(PRAYER_LABEL_KEYS[p])} ${t("kaza_increase")}`}
                            disabled={remaining <= 0 || savingDay}
                            onClick={() => setDayDraft((prev) => stepKazaDraft(server, prev || {}, p, 1))}
                            className="!bg-terakkiPrimaryDark !text-white disabled:!opacity-40"
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ) : (
                        <span className={`text-md ${count > 0 ? `font-medium ${cls.accent}` : cls.muted}`}>{count}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="flex items-center justify-between px-5 py-3">
                <span className={`text-base ${cls.muted}`}>{t("kaza_prayed")}</span>
                <span className={`text-md font-medium ${cls.title}`}>{sumKaza(draft, dayPrayers)}</span>
              </div>
              {!dayEditable ? (
                <p className={`px-5 pb-4 text-[13px] ${cls.muted}`}>{t("kaza_view_only")}</p>
              ) : null}
            </div>
          )
        ) : isError && !saved ? (
          <ConnectionState
            error={error}
            onRetry={refetch}
            retrying={isFetching}
            message={t("kaza_load_failed")}
            compact
          />
        ) : (
          <>
            <div className={cls.row}>
              <SectionTitle>{t("kaza_mezhep")}</SectionTitle>
              <div className="px-5 flex flex-wrap gap-2" role="radiogroup" aria-label={t("kaza_mezhep")}>
                {MEZHEPS.map((m) => {
                  const active = m.id === mezhep;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      disabled={!ready}
                      onClick={() => setMezhep(m.id)}
                      className={`px-4 py-1.5 rounded-full text-base transition-colors ${
                        active
                          ? "bg-terakkiPrimaryDark text-white dark:bg-white dark:text-terakkiPrimaryDark"
                          : `${cls.chip} hover:bg-terakkiBrandMist`
                      }`}
                    >
                      {t(m.key)}
                    </button>
                  );
                })}
              </div>
              <p className={`px-5 pt-2 pb-4 text-[13px] ${cls.muted}`}>{t("kaza_vitir_hint")}</p>
            </div>

            <div className={cls.row}>
              <SectionTitle>{t("kaza_by_prayer")}</SectionTitle>
              <ul>
                {(ready ? prayers : KAZA_PRAYERS).map((p) => (
                  <li key={p} className={`flex items-center justify-between gap-3 px-5 py-2.5 border-t border-terakkiBorder dark:border-white/[0.14]`}>
                    <label htmlFor={`kaza-${p}`} className={`text-[15px] ${cls.title}`}>
                      {t(PRAYER_LABEL_KEYS[p])}
                    </label>
                    {ready ? (
                      <input
                        id={`kaza-${p}`}
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="0"
                        value={counts[p] ? String(counts[p]) : ""}
                        onChange={(e) => setCounts((prev) => ({ ...prev, [p]: toCount(e.target.value) }))}
                        className={`${inputCls} w-28`}
                      />
                    ) : (
                      <span className={`h-10 w-28 rounded-xl ${cls.skeleton}`} />
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="pb-5">
              <SectionTitle>{t("kaza_add_by_years")}</SectionTitle>
              <p className={`px-5 pb-3 text-[13px] ${cls.muted}`}>{t("kaza_add_by_years_hint")}</p>
              <div className="px-5 flex flex-wrap items-end gap-3">
                <label className="flex-1 min-w-[96px] flex flex-col gap-1">
                  <span className={`text-[13px] ${cls.muted}`}>{t("kaza_years")}</span>
                  <input
                    inputMode="numeric"
                    maxLength={3}
                    placeholder="0"
                    value={years}
                    disabled={!ready}
                    onChange={(e) => setYears(e.target.value.replace(/[^0-9]/g, ""))}
                    className={`${inputCls} w-full min-w-0`}
                  />
                </label>
                <label className="flex-1 min-w-[96px] flex flex-col gap-1">
                  <span className={`text-[13px] ${cls.muted}`}>{t("kaza_days")}</span>
                  <input
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="0"
                    value={days}
                    disabled={!ready}
                    onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ""))}
                    className={`${inputCls} w-full min-w-0`}
                  />
                </label>
                <SoftButton
                  tone="soft"
                  startIcon={<AddIcon />}
                  disabled={addDays <= 0}
                  onClick={applyYearsDays}
                  sx={{ height: 40, whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {t("kaza_add")}
                </SoftButton>
              </div>
              {addDays > 0 ? (
                <p className={`px-5 pt-2 text-[13px] ${cls.muted}`}>{`+${addDays.toLocaleString("tr-TR")} × ${prayers.length}`}</p>
              ) : null}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
